import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { paths } from 'src/routes/paths';

import { findSurveyUrl } from 'src/utils/check-in-survey';
import { markCheckInRedirected } from 'src/utils/check-in-redirect';

import { recordCheckIn, getCheckInStatus, type CheckInStatusResponse } from 'src/api/check-ins';

export const checkInKeys = {
  status: ['check-ins', 'status'] as const,
};

export function useCheckInStatus(enabled = true) {
  return useQuery({
    queryKey: checkInKeys.status,
    queryFn: getCheckInStatus,
    enabled,
  });
}

export function useRecordCheckIn() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: recordCheckIn,
    onSuccess: (record) => {
      // Update the guard synchronously so completion cannot race navigation to
      // the confirmation page (and Skip cannot bounce back from the dashboard).
      queryClient.setQueryData<CheckInStatusResponse>(checkInKeys.status, (current) => {
        if (!current) return current;

        return {
          ...current,
          recordedNumbers: [...new Set([...current.recordedNumbers, record.checkInNumber])],
          dueCheckInNumber: null,
        };
      });

      void queryClient.invalidateQueries({ queryKey: checkInKeys.status });
    },
    onError: () => {
      // A rejected record usually means the milestone stopped being due server-side.
      // Refetching releases the leave-blocker, which reads the cached due number and
      // would otherwise hold the user on a page whose only exits all fail.
      void queryClient.invalidateQueries({ queryKey: checkInKeys.status });
    },
  });
}

/**
 * When a milestone check-in is due and has a survey to show, send the user to its
 * page — once per milestone, and only after the activity count has grown, so finishing
 * one check-in never chains straight into the next. Reaching the page by URL is still
 * handled there, including its Skip action.
 */
export function useCheckInTrigger(enabled = true) {
  const navigate = useNavigate();
  const { data } = useCheckInStatus(enabled);

  useEffect(() => {
    if (!enabled || !data) return;
    const due = data.dueCheckInNumber;
    if (!due) return;
    // An unconfigured milestone has nothing to show, and opening it would spend the
    // one redirect the user gets for it. Leave it due until a survey URL exists.
    if (!findSurveyUrl(due)) return;
    if (!markCheckInRedirected(due, data.totalActivitiesCompleted)) return;
    navigate(`${paths.checkIn}?number=${due}`, { replace: true });
  }, [data, enabled, navigate]);
}
